/**
 * SEO Constants - Default titles, descriptions, and keywords
 */

export const BASE_URL = 'https://1crew3.com';

export const DEFAULT_SEO = {
  title: '1crew3 — Yacht Crew Jobs & Recruitment Platform',
  description: 'Find yacht crew jobs or hire qualified crew for your yacht. 1crew3 connects captains and crew across the Mediterranean, Caribbean, and global yachting hubs.',
  image: `${BASE_URL}/og-image.png`,
  siteName: '1crew3',
  twitterHandle: '@1crew3'
};

export const PAGE_SEO = {
  home: {
    title: 'Yacht Crew Jobs & Hire Crew — 1crew3',
    description: 'Find yacht crew jobs worldwide or hire qualified crew for your yacht. Free for crew members. Join 1crew3 — the modern yacht crew recruitment platform.'
  },
  jobs: {
    title: 'Yacht Crew Jobs — Deckhand, Stewardess, Chef & Captain Positions — 1crew3',
    description: 'Browse open yacht crew jobs on superyachts and motor yachts. Deckhand, stewardess, chef, engineer, and captain positions in the Med, Caribbean & worldwide.'
  },
  crew: {
    title: 'Find Yacht Crew — Search Qualified Candidates — 1crew3',
    description: 'Search qualified yacht crew by position, certification, experience, and availability. Find your next deckhand, stewardess, chef, or captain.'
  },
  registerCrew: {
    title: 'Sign Up as Yacht Crew — Free — 1crew3',
    description: 'Create your free yacht crew profile. Get discovered by captains and yacht owners hiring worldwide. Upload your CV and start applying today.'
  },
  registerOwner: {
    title: 'Hire Yacht Crew — Captain & Owner Registration — 1crew3',
    description: 'Post yacht crew positions and find qualified candidates. Free until June 1st. The fast, modern way to recruit crew for your yacht.'
  },
  login: {
    title: 'Sign In — 1crew3',
    description: 'Sign in to your 1crew3 account to browse yacht crew jobs or manage your crew recruitment.'
  },
  about: {
    title: 'About 1crew3 — Modern Yacht Crew Recruitment',
    description: '1crew3 is the modern platform connecting yacht crew with captains and owners worldwide. Built by people who understand yachting.'
  },
  blog: {
    title: 'Yacht Crew Blog — Career Tips, Industry News — 1crew3',
    description: 'Yacht crew career advice, industry news, salary guides, and certification tips. Everything you need for a career in yachting.'
  },
  billing: {
    title: 'Subscription & Billing — 1crew3',
    description: 'Manage your 1crew3 subscription. View plans, update payment methods, and manage your account.'
  },
  dashboard: {
    title: 'Dashboard — 1crew3',
    description: 'Your 1crew3 dashboard. Manage your profile, applications, and job postings.'
  }
};

// Position-specific SEO
export const POSITION_SEO = {
  deckhand: {
    title: 'Yacht Deckhand Jobs — Entry Level & Experienced — 1crew3',
    description: 'Find deckhand positions on superyachts and motor yachts. Entry-level and experienced deckhand jobs in the Mediterranean, Caribbean, and worldwide.'
  },
  stewardess: {
    title: 'Yacht Stewardess Jobs — Interior Crew Positions — 1crew3',
    description: 'Browse yacht stewardess and interior crew positions. Chief stewardess, 2nd stewardess, and junior stewardess jobs on luxury yachts.'
  },
  chef: {
    title: 'Yacht Chef Jobs — Superyacht Galley Positions — 1crew3',
    description: 'Find yacht chef positions on superyachts. Head chef, sous chef, and cook jobs. Competitive salaries and worldwide locations.'
  },
  engineer: {
    title: 'Yacht Engineer Jobs — Chief & Junior Engineer Positions — 1crew3',
    description: 'Search yacht engineer positions. Chief engineer, 2nd engineer, and junior engineer jobs on motor yachts and superyachts.'
  },
  captain: {
    title: 'Yacht Captain Jobs — Master & Skipper Positions — 1crew3',
    description: 'Find captain and skipper positions on yachts. Master, relief captain, and delivery skipper jobs worldwide.'
  },
  bosun: {
    title: 'Yacht Bosun Jobs — Deck Supervisor Positions — 1crew3',
    description: 'Browse bosun and lead deckhand positions on superyachts. Deck department leadership roles with competitive salaries.'
  },
  purser: {
    title: 'Yacht Purser Jobs — Administration & Finance Positions — 1crew3',
    description: 'Find yacht purser and administrative positions. Manage yacht accounts, crew paperwork, and provisioning on superyachts.'
  }
};

// Location-specific SEO
export const LOCATION_SEO = {
  mediterranean: {
    title: 'Mediterranean Yacht Crew Jobs — Med Season Positions — 1crew3',
    description: 'Find yacht crew jobs in the Mediterranean. France, Italy, Spain, Greece, Croatia, Turkey. Seasonal and permanent positions.'
  },
  caribbean: {
    title: 'Caribbean Yacht Crew Jobs — Winter Season Positions — 1crew3',
    description: 'Browse yacht crew positions in the Caribbean. St. Maarten, Antigua, BVI, USVI, Bahamas. Winter season opportunities.'
  },
  florida: {
    title: 'Florida Yacht Crew Jobs — Fort Lauderdale & Palm Beach — 1crew3',
    description: 'Find yacht crew jobs in Florida. Fort Lauderdale, Palm Beach, Miami. Full-time and seasonal positions available.'
  },
  dubai: {
    title: 'Dubai & Middle East Yacht Crew Jobs — 1crew3',
    description: 'Search yacht crew positions in Dubai and the Middle East. Competitive tax-free salaries on luxury superyachts.'
  },
  australia: {
    title: 'Australia Yacht Crew Jobs — Pacific Yachting Positions — 1crew3',
    description: 'Find yacht crew jobs in Australia and the Pacific. Sydney, Gold Coast, Great Barrier Reef charter positions.'
  }
};

// Blog post slugs and metadata
export const BLOG_POSTS = [
  {
    slug: 'yacht-crew-salary-guide',
    title: 'Yacht Crew Salary Guide 2026 — What Every Position Pays',
    description: 'Complete salary guide for yacht crew positions in 2026. Deckhand, stewardess, chef, engineer, and captain salaries by yacht size.',
    targetQuery: 'yacht crew salary, how much do yacht crew make'
  },
  {
    slug: 'how-to-become-yacht-crew',
    title: 'How to Become Yacht Crew — Complete Beginner\'s Guide',
    description: 'Step-by-step guide to starting a career in yachting. Certifications, training, and how to land your first yacht crew job.',
    targetQuery: 'how to become yacht crew, how to get a job on a yacht'
  },
  {
    slug: 'stcw-certification-guide',
    title: 'STCW Certification Guide — Requirements, Cost & Where to Get Certified',
    description: 'Everything you need to know about STCW certification. Requirements, costs, course duration, and training centers worldwide.',
    targetQuery: 'STCW certification, STCW course'
  },
  {
    slug: 'yacht-crew-cv-template',
    title: 'Yacht Crew CV Template — Free Download & Writing Guide',
    description: 'Download free yacht crew CV templates. Expert tips on writing a CV that gets you hired on yachts.',
    targetQuery: 'yacht crew CV, yacht crew resume'
  },
  {
    slug: 'best-yacht-crew-agencies',
    title: 'Best Yacht Crew Agencies & Recruitment Platforms in 2026',
    description: 'Compare the top yacht crew agencies and recruitment platforms. Find the best services for your yachting career.',
    targetQuery: 'yacht crew agency, yacht crew recruitment'
  },
  {
    slug: 'deckhand-job-description',
    title: 'Yacht Deckhand Job Description — Duties, Skills & Salary',
    description: 'What does a yacht deckhand do? Complete job description, required skills, certifications, and salary expectations.',
    targetQuery: 'deckhand job description, yacht deckhand'
  },
  {
    slug: 'stewardess-job-description',
    title: 'Yacht Stewardess Job Description — Duties, Skills & Salary',
    description: 'Complete guide to working as a yacht stewardess. Daily duties, skills required, and salary by experience level.',
    targetQuery: 'yacht stewardess job, stewardess duties'
  },
  {
    slug: 'yacht-chef-job-guide',
    title: 'Yacht Chef Job Guide — Qualifications, Salary & What to Expect',
    description: 'Become a yacht chef. Required qualifications, expected salary, and what life is really like cooking on superyachts.',
    targetQuery: 'yacht chef job, superyacht chef salary'
  },
  {
    slug: 'yacht-captain-hiring-guide',
    title: 'How to Hire Yacht Crew — Captain\'s Complete Guide',
    description: 'Captain\'s guide to hiring yacht crew. Best practices for finding, interviewing, and selecting qualified crew members.',
    targetQuery: 'hire yacht crew, find yacht crew'
  },
  {
    slug: 'mediterranean-yacht-jobs',
    title: 'Mediterranean Yacht Crew Jobs — Season Guide & Where to Look',
    description: 'Guide to finding yacht crew jobs in the Mediterranean. Best ports, timing, and how to prepare for Med season.',
    targetQuery: 'Mediterranean yacht jobs, Med season yacht crew'
  },
  {
    slug: 'caribbean-yacht-jobs',
    title: 'Caribbean Yacht Crew Jobs — Season Guide & Hiring Timeline',
    description: 'Find yacht crew positions in the Caribbean. Winter season timeline, best islands for crew, and hiring tips.',
    targetQuery: 'Caribbean yacht jobs'
  },
  {
    slug: 'day-in-the-life-yacht-crew',
    title: 'A Day in the Life of Yacht Crew — What It\'s Really Like',
    description: 'Honest look at daily life working on a superyacht. Routines, challenges, and rewards of a yachting career.',
    targetQuery: 'yacht crew life, working on a superyacht'
  },
  {
    slug: 'eng1-certification-guide',
    title: 'ENG1 Medical Certificate Guide — Requirements & Where to Get One',
    description: 'Guide to obtaining your ENG1 medical certificate. Requirements, costs, approved doctors, and validity period.',
    targetQuery: 'ENG1 medical, yacht crew medical'
  },
  {
    slug: 'yacht-crew-interview-tips',
    title: 'Yacht Crew Interview Tips — How to Land Your First Job',
    description: 'Expert tips for yacht crew interviews. What to wear, common questions, and how to impress captains and owners.',
    targetQuery: 'yacht crew interview'
  },
  {
    slug: 'yacht-size-crew-requirements',
    title: 'How Many Crew Does a Yacht Need? Crew-to-Size Guide',
    description: 'Guide to crew requirements by yacht size. How many crew on a 30m, 50m, or 80m yacht? Staffing ratios explained.',
    targetQuery: 'yacht crew requirements, how many crew on a superyacht'
  }
];

// Keywords for different contexts
export const KEYWORDS = {
  primary: [
    'yacht crew jobs',
    'superyacht jobs',
    'yacht deckhand jobs',
    'yacht stewardess jobs',
    'yacht chef jobs',
    'yacht engineer jobs',
    'yacht captain jobs',
    'work on a yacht',
    'hire yacht crew',
    'find yacht crew',
    'yacht crew recruitment'
  ],
  positions: [
    'deckhand',
    'stewardess',
    'chief stewardess',
    'purser',
    'bosun',
    'first officer',
    'captain',
    'yacht chef',
    'engineer',
    'ETO'
  ],
  locations: [
    'Mediterranean',
    'Caribbean',
    'Florida',
    'Fort Lauderdale',
    'Antibes',
    'Palma de Mallorca',
    'Dubai'
  ]
};
